/**
 * ONE derivation of "this line is too small to be partially harvested", for surfaces that
 * hold raw position rows rather than the enriched get_portfolio_view payload.
 *
 * WHY THIS EXISTS.
 * The Portfolio page does NOT compute this — it reads a flag the backend already set:
 *
 *   HelionBackend/strategy/portfolio_view.py:2356-2366
 *       min_initial = int((home_run_payload.get("policy") or {}).get("min_initial_contracts") or 2)
 *       for row in held_rows_raw:
 *           _qty = _to_int(row.get("quantity"), 0) or 0
 *           if _qty > 0 and _qty < min_initial:
 *               row["home_run_single_contract_constraint"] = True
 *               row["home_run_can_partial_trim"] = False
 *               row["home_run_min_contracts_for_trim"] = min_initial
 *
 * which PortfolioView.tsx:2719-2720 maps to singleContractConstraint / minContractsForTrim
 * and renders at PortfolioView.tsx:1418 as the "No partial trim" badge.
 *
 * The Signals holdings table is fed by get_leaps_risk_snapshot, a greeks/risk snapshot that
 * carries no home-run fields at all. Rather than add a backend contract for a display-only
 * flag, Signals derives it here from the SAME policy object it already fetches for the
 * harvest panel. This function is a transcription of the Python above — if that changes,
 * change this, and the tests cite it so the drift is caught.
 *
 * ---------------------------------------------------------------------------------------
 * READ THE THRESHOLD, NEVER HARDCODE 2.
 *
 * `min_initial_contracts` reaches the payload from entry_signals.json ->
 * construction.min_contracts_for_home_run_trim, via home_run_redeployment.py:148-149. It is
 * configuration and it can move.
 *
 * THE CAVEAT THAT USED TO LIVE HERE IS NOW CLOSED (backend, 2026-09-09).
 * min_contracts_for_home_run_trim is a CONSTRUCTION rule — it sizes NEW lines so they will
 * be trimmable later. It is NOT the gate that blocks a trim at exit time. That gate is
 * exit_signals.py, and it is TRIM-PCT DEPENDENT:
 *
 *     trim_qty = max(0, min(int(qty * trim_pct), qty - min_runner_contracts))
 *
 * Both constants read 2 at today's 50%/runner-1 settings and agree with the truth, which is
 * why the discrepancy went unnoticed. At a 40% trim they would not: a 2-lot gives
 * int(2*0.4)=0 and goes dark, so the answer is 3 while both constants still say 2 — the
 * engine's own untrimmable_line_count puts that at 18 of 28 lines rather than 10.
 *
 * The backend now derives it once (strategy/utils.py min_trimmable_quantity) and publishes
 * it as policy.min_contracts_for_trim, which BOTH this page and the Portfolio badge read.
 * Prefer that field. min_initial_contracts remains only as a fallback for a payload served
 * by a backend older than the fix, and 2 as the last resort. Do not reintroduce a local
 * derivation: integration_tests/test_min_trimmable_quantity.py guards the backend one.
 *
 * ---------------------------------------------------------------------------------------
 * NEVER GUESS WHEN THE POLICY DID NOT LOAD.
 *
 * build_home_run_dashboard_payload falls back to {policy:{}, summary:{...}} on any
 * exception, so an empty policy means "we do not know", not "no constraint". A boolean
 * `enabled` is the marker that the policy actually loaded — the same test
 * HarvestDecisionPanel.tsx:622 uses. Returning null in that case makes the caller render
 * nothing rather than assert a reassuring "this line is fine".
 */

export type LotConstraintPolicy = {
  enabled?: boolean | null;
  /** The DERIVED exit-time gate. Preferred: it accounts for the trim percentage. */
  min_contracts_for_trim?: number | null;
  /** The construction constant. Fallback only — see the header. */
  min_initial_contracts?: number | null;
};

export type LotConstraint = {
  /** True when this line is too small to split: 0 < quantity < minContracts. */
  blocked: boolean;
  /** The policy minimum actually read (never a hardcoded default when policy loaded). */
  minContracts: number;
  /** The quantity the verdict was reached on. */
  quantity: number;
};

/** The backend's own fallback when min_initial_contracts is absent from a LOADED policy. */
const MIN_INITIAL_FALLBACK = 2;

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

/**
 * Whether the harvest policy actually loaded. Mirrors HarvestDecisionPanel.tsx:622 — an
 * exception fallback ships `policy: {}`, and `{}` must not read as "policy says no limit".
 */
export const lotPolicyLoaded = (policy: LotConstraintPolicy | null | undefined): boolean =>
  typeof policy?.enabled === "boolean";

/**
 * Returns null when we cannot honestly answer — policy did not load, or the quantity is
 * missing/non-positive (a zero or negative row is not a held lot). Otherwise reports whether
 * the line is below the policy minimum for a partial trim.
 *
 * Transcribed from portfolio_view.py:2358-2363. Display only: it stages nothing, and it
 * never touches the trim arithmetic, the 2x trigger, or the candidate list.
 */
export function lotHarvestConstraint(
  quantity: unknown,
  policy: LotConstraintPolicy | null | undefined
): LotConstraint | null {
  if (!lotPolicyLoaded(policy)) return null;

  const qty = isFiniteNumber(quantity) ? Math.trunc(quantity) : NaN;
  if (!Number.isFinite(qty) || qty <= 0) return null;

  // Prefer the derived exit-time gate; fall back to the construction constant only for a
  // payload from a backend older than the 2026-09-09 fix, then to the engine default.
  // Mirrors portfolio_view.py, so the two badges cannot disagree.
  const candidates = [policy?.min_contracts_for_trim, policy?.min_initial_contracts];
  const found = candidates.find((v) => isFiniteNumber(v) && v >= 1);
  const minContracts = isFiniteNumber(found) ? Math.trunc(found) : MIN_INITIAL_FALLBACK;

  return { blocked: qty < minContracts, minContracts, quantity: qty };
}
